/**
 * Field Help — Recurring Transactions
 *
 * Keys MUST match the field ids in
 * `features/recurring-transactions/components/RecurringTransactionsConfig.ts`
 * (RECURRING_TRANSACTIONS_CONFIG.fields) so the "?" popover attaches to the
 * right column header / editor row / card field.
 *
 * Registry key is the HYPHENATED entity value ("recurring-transactions") the
 * config carries (`entity:` in the Config), which is what `config.entity` passes
 * to FieldHelpPopover — NOT the underscore table name.
 *
 * A recurring transaction is a monthly line that the QuickBooks monthly-invoice
 * cron turns into an invoice in a specific realm. `quickbooks_realm_id` decides
 * whether it bills the West Tennessee Consulting company or Schooley Mitchell.
 */

import type { EntityFieldHelp } from "./types";

export const recurringTransactionFieldHelp: EntityFieldHelp = {
  quickbooks_realm_id: {
    title: "Billing Company (QuickBooks Realm)",
    description:
      "Which QuickBooks company this recurring line invoices — West Tennessee Consulting or Schooley Mitchell. Shown as a colored badge.",
    usage:
      "Set this when creating the line so the monthly-invoice cron bills the right company. WTC = our own consulting/MSP services; Schooley Mitchell = the cost-savings program. They are separate books — never mix.",
    relatedTo:
      "Maps to a connected QuickBooks realm. Friendly labels are managed in Admin → Settings → QuickBooks Realm Labels.",
  },
  item: {
    title: "Item",
    description: "The product/service name that appears as the invoice line item.",
    usage: "Use the client-facing name you want on the invoice, e.g. \"Website Hosting\" or \"Managed IT — Monthly\".",
  },
  customer_id: {
    title: "Customer",
    description: "The contact this recurring line bills. Optional — a company-only line has no contact.",
    usage:
      "Billing derives the QuickBooks customer from the company, so a contact isn't required. Set it when a specific person should appear on the invoice.",
    relatedTo: "recurring transaction → contact (optional) and → company.",
  },
  company_id: {
    title: "Company",
    description: "The client company this recurring line bills. Drives which QuickBooks customer the invoice goes to.",
    usage: "Set the company so billing can resolve the QuickBooks customer, even when no contact is linked.",
    relatedTo: "recurring transaction → company → QuickBooks customer.",
  },
  amount: {
    title: "Amount",
    description: "The per-unit price for this line, before quantity.",
    usage: "Line total on the invoice = amount × quantity, per BILLING PERIOD (monthly by default; the yearly price for an annual line). Enter the unit price, not the line total, when quantity > 1.",
  },
  quantity: {
    title: "Quantity",
    description: "How many units of the item bill each cycle. Defaults to 1.",
    usage: "Invoice line total = amount × quantity.",
  },
  description: {
    title: "Description",
    description: "Optional notes or a longer description for the invoice line.",
    usage: "Use for detail the item name doesn't capture. Optional.",
  },
  sync_status: {
    title: "Sync Status",
    description: "Whether this month's invoice has been created in QuickBooks: pending, synced, or failed.",
    usage: "Read-only. \"failed\" means the last monthly-invoice run couldn't bill it — check the error on the detail page.",
  },
  last_synced_at: {
    title: "Last Synced",
    description: "When this line was last turned into a QuickBooks invoice.",
    usage: "Read-only. Blank means it hasn't been invoiced yet.",
  },
  last_synced_month: {
    title: "Last Synced Month",
    description: "The billing month (YYYY-MM) this line was last invoiced for.",
    usage: "Read-only. The monthly-invoice cron uses this to avoid double-billing the same month.",
  },
  quickbooks_id: {
    title: "QuickBooks ID",
    description: "The id of the invoice created for this line in QuickBooks.",
    usage: "Read-only. \"Not synced\" means no invoice exists yet.",
  },
};
